import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { getAlternatePath } from '../config/routeMap';

/**
 * <LanguageSwitcher />
 *
 * Toggles between the FR and EN variants of the CURRENT page (Decision 10
 * from the pre-publication report §6.2). If the user is on
 * `/fr/etui-hydrocortisone`, clicking EN sends them to
 * `/en/hydrocortisone-case` — NOT to the homepage.
 *
 * On the x-default root `/`, the switcher acts as a plain "go to /en" (since
 * `/` has no localized equivalent by design).
 */
const LanguageSwitcher = ({ className = '', testId = 'lang-switcher' }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = location.pathname.startsWith('/fr')
    ? 'fr'
    : location.pathname.startsWith('/en')
      ? 'en'
      : null; // root path

  // On root: default the visible label to the browser preference so the
  // button always shows the LANGUAGE THE USER IS ABOUT TO SWITCH TO.
  const targetLang = currentLang === 'fr' ? 'en' : 'fr';

  const handleClick = () => {
    if (currentLang === null) {
      // On root x-default: send user to the target language homepage.
      navigate(targetLang === 'fr' ? '/fr' : '/en');
      return;
    }
    const target = getAlternatePath(location.pathname, targetLang);
    navigate(target);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`flex items-center space-x-1 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors ${className}`}
      data-testid={testId}
      aria-label={targetLang === 'fr' ? 'Passer en français' : 'Switch to English'}
    >
      <Globe className="w-4 h-4 text-neutral-300" aria-hidden="true" />
      <span className="text-sm font-medium text-neutral-200 uppercase">
        {targetLang}
      </span>
    </button>
  );
};

export default LanguageSwitcher;
